// Last updated: 2026-06-10T00:00:00Z (Chakra v3 migration)
import React, { useEffect, useState } from 'react';
import { Link } from 'react-router-dom';
import { LuMoon, LuSun, LuBell } from 'react-icons/lu';
import { FaShieldAlt, FaBars, FaShoppingCart } from 'react-icons/fa';
import { FiLogOut, FiLogIn, FiGrid } from 'react-icons/fi';
import md5 from "blueimp-md5";
import { auth, db } from "./firebase";
import { signOut } from "firebase/auth";
import {
  Box,
  Flex,
  Button,
  Text,
  Avatar,
  IconButton,
  HStack,
  Link as ChakraLink,
  useBreakpointValue,
  Badge,
} from '@chakra-ui/react';
import { useColorMode } from './components/ui/color-mode';
import { toaster } from './components/ui/toaster';
import { useAuthContext } from './AuthProvider';
import { useShoppingCart } from './ShoppingCartContext';
import { collection, query, where, getDocs } from 'firebase/firestore';

const defaultAvatar = "https://ui-avatars.com/api/?name=G&background=e5e7eb&color=232946&bold=true";

function getGravatarUrl(email) {
  if (!email) return defaultAvatar;
  const hash = md5(email.trim().toLowerCase());
  return `https://www.gravatar.com/avatar/${hash}?d=identicon`;
}

// =============================================
// Header Component with Guest Migration Notifications
// Last updated: Fri Jul  4 02:18:16 UTC 2025
//
// Features:
// - Guest header display with login button for guests
// - Migration success notification via toast when guest results are migrated
// - Exam notification system for purchased but untouched exams
// Console logs are added for debugging.
// =============================================

const Header = ({ onOpenSidebar }) => {
    const { user, userProfile, userType } = useAuthContext();
    const { colorMode, toggleColorMode } = useColorMode();
    const [hasNewExamNotification, setHasNewExamNotification] = useState(false);
    const { getItemCount } = useShoppingCart();
    const isMobile = useBreakpointValue({ base: true, md: false });
    
    // Fallback mobile detection for cart icon visibility
    const [isMobileState, setIsMobileState] = useState(false);
    
    useEffect(() => {
        const checkMobile = () => {
            setIsMobileState(window.innerWidth < 768);
        };
        
        checkMobile();
        window.addEventListener('resize', checkMobile);
        return () => window.removeEventListener('resize', checkMobile);
    }, []);

    useEffect(() => {
        if (!user) return;
        const checkUntouchedExams = async () => {
            // Get all purchased exams
            const purchasesSnap = await getDocs(query(
                collection(db, 'purchases'),
                where('userId', '==', user.uid),
                where('status', '==', 'succeeded')
            ));
            const purchasedExamIds = [...new Set(purchasesSnap.docs.map(doc => doc.data().examId))];
            if (purchasedExamIds.length === 0) {
                setHasNewExamNotification(false);
                return;
            }
            // Get all attempts for this user
            const attemptsSnap = await getDocs(query(
                collection(db, 'attempts'),
                where('userId', '==', user.uid)
            ));
            const attemptedExamIds = new Set();
            attemptsSnap.docs.forEach(doc => {
                const d = doc.data();
                if (d.questionSetId) attemptedExamIds.add(d.questionSetId);
                if (d.questionIds && Array.isArray(d.questionIds)) {
                    d.questionIds.forEach(id => attemptedExamIds.add(id));
                }
            });
            // If any purchased examId is not in attemptedExamIds, show notification
            const hasUntouched = purchasedExamIds.some(id => !attemptedExamIds.has(id));
            setHasNewExamNotification(hasUntouched);
        };
        checkUntouchedExams();
    }, [user]);

    // Check for migration success and show notification
    useEffect(() => {
        // Only show migration notification for authenticated users
        if (!user) return;

        const migrationSuccessData = localStorage.getItem('migrationSuccess');
        if (migrationSuccessData) {
            try {
                const migrationInfo = JSON.parse(migrationSuccessData);
                const migrationTimestamp = new Date(migrationInfo.timestamp);
                const now = new Date();
                const timeDiff = now - migrationTimestamp;
                
                // Only show notification if migration happened in the last 24 hours
                if (timeDiff < 24 * 60 * 60 * 1000) {
                    console.log('[Header] Showing migration success notification');
                    toaster.success({
                        title: "Welcome back! 🎉",
                        description: `We've successfully transferred ${migrationInfo.count} exam result${migrationInfo.count > 1 ? 's' : ''} from your guest session to your account.`,
                        duration: 8000,
                    });
                }
                
                // Clear the notification after showing
                localStorage.removeItem('migrationSuccess');
            } catch (error) {
                console.error('[Header] Error parsing migration success data:', error);
                localStorage.removeItem('migrationSuccess');
            }
        }
    }, [user]);

    const handleLogout = async () => {
        try {
            await signOut(auth);
            // The AuthProvider will handle navigation
        } catch (error) {
            console.error("Error signing out:", error);
        }
    };

    const isLoggedIn = !!user;
    // Detect guest user: Firebase anonymous, or userProfile.role/userType is 'guest'
    const isGuest = (
      (user && user.isAnonymous) ||
      (userProfile && (userProfile.role === 'guest' || userProfile.userType === 'guest')) ||
      userType === 'guest'
    );
    if (isLoggedIn) {
      console.log('[Header] User is logged in. isGuest:', isGuest, 'user:', user, 'userProfile:', userProfile, 'userType:', userType);
    } else {
      console.log('[Header] No user logged in.');
    }
    // Name logic: prefer user.displayName, then profile name, then email, then Guest
    const name = (() => {
        if (user && user.displayName) return user.displayName;
        const first = userProfile?.firstName?.trim() || '';
        const last = userProfile?.lastName?.trim() || '';
        const computed = (first + ' ' + last).trim() || 'Not set';
        console.log('[Header] User name:', computed);
        return computed;
    })();
    // Avatar logic: prefer Google photoURL, then profile.avatar, then Gravatar, then default
    const avatar = (user && user.photoURL) || (userProfile && userProfile.avatar) || (user && user.email ? getGravatarUrl(user.email) : defaultAvatar);

    return (
        <Box
            as="header"
            position="sticky"
            top="0"
            zIndex="sticky"
            bg={colorMode === 'dark' ? 'rgba(17, 19, 23, 0.92)' : 'rgba(255, 255, 255, 0.92)'}
            backdropFilter="blur(8px)"
            borderBottom="1px solid"
            borderColor={colorMode === 'dark' ? 'gray.700' : 'gray.200'}
            boxShadow={colorMode === 'dark' ? '0 1px 3px rgba(0,0,0,0.3)' : '0 1px 3px rgba(0,0,0,0.04)'}
            fontFamily="body"
        >
            <Flex
                alignItems={{ base: 'stretch', md: 'center' }}
                justifyContent="space-between"
                px={{ base: 4, md: 12 }}
                py={{ base: 1.5, md: 1.5 }}
                height={{ base: 'auto', md: '64px' }}
                direction={{ base: 'row' }}
                flexWrap={{ base: 'wrap', md: 'nowrap' }}
            >
                <IconButton
                    aria-label="Open menu"
                    variant="ghost"
                    size="md"
                    display={{ base: 'flex', md: 'none' }}
                    onClick={onOpenSidebar}
                    border="1px solid"
                    borderColor={colorMode === 'dark' ? 'gray.600' : 'gray.200'}
                    borderRadius="lg"
                    mr={2}
                >
                    <FaBars />
                </IconButton>
                <Flex alignItems="center" gap={1} mb={{ base: 2, md: 0 }} pl={{ base: 0, md: 0 }}>
                    <ChakraLink
                        as={Link}
                        to="/"
                        _hover={{ textDecoration: 'none' }}
                        _focus={{ outline: 'none', boxShadow: 'none' }}
                        _focusVisible={{ outline: 'none', boxShadow: 'none' }}
                        alignItems="center"
                        gap={2}
                    >
                        <Flex alignItems="center" gap={{ base: 1, md: 2 }} whiteSpace="nowrap" width="auto">
                            <Box fontSize={{ base: '24px', md: '32px' }} color={colorMode === 'dark' ? 'brand.300' : 'brand.600'} display={{ base: 'none', md: 'flex' }} alignItems="center">
                                <FaShieldAlt />
                            </Box>
                            <Text as="span" color={colorMode === 'dark' ? 'brand.200' : 'brand.700'} fontSize={{ base: '24px', md: '32px' }} fontWeight="700" fontFamily="'Play', sans-serif" letterSpacing="-1px">examgate</Text>
                            <Text as="span" color={colorMode === 'dark' ? 'accent.400' : 'accent.600'} fontSize={{ base: '24px', md: '32px' }} fontWeight="700" fontFamily="'Play', sans-serif" letterSpacing="-1px">.net</Text>
                        </Flex>
                    </ChakraLink>
                </Flex>
                <HStack gap={{ base: 1, md: 3 }} flexWrap="nowrap" alignItems="center" ml={2} flexShrink={0}>
                    <IconButton
                        aria-label="Open menu"
                        variant="ghost"
                        size="md"
                        display={{ base: 'none', md: 'none' }}
                        onClick={onOpenSidebar}
                        bg={colorMode === 'dark' ? 'gray.700' : 'yellow.100'}
                    >
                        <FaBars />
                    </IconButton>
                    {/* Theme toggle - hide in mobile when cart is visible */}
                    {(!isMobileState || getItemCount() === 0) && (
                        <IconButton
                            variant="ghost"
                            size="md"
                            aria-label="Toggle theme"
                            border="1px solid"
                            borderColor={colorMode === 'dark' ? 'gray.600' : 'gray.200'}
                            borderRadius="lg"
                            onClick={toggleColorMode}
                            _hover={{ bg: colorMode === 'dark' ? 'whiteAlpha.100' : 'blackAlpha.50' }}
                            transition="all 0.2s"
                        >
                            {colorMode === 'dark' ? <LuSun size={16} color="orange.400" /> : <LuMoon size={16} color="orange.500" />}
                        </IconButton>
                    )}
                    {/* Show Login button for guests, otherwise show user info and logout */}
                    {isLoggedIn && isGuest ? (
                        <Button
                            as={Link}
                            to="/login"
                            colorPalette="blue"
                            variant="outline"
                            size="md"
                            width={isMobile ? '40px' : undefined}
                            minWidth={isMobile ? '40px' : undefined}
                            justifyContent="center"
                            p={isMobile ? 0 : undefined}
                        >
                            <FiLogIn />
                            {!isMobile && ' Login'}
                        </Button>
                    ) : isLoggedIn ? (
                        <HStack gap={3} flexWrap={{ base: 'wrap', md: 'nowrap' }} alignItems="center">
                            <HStack gap={2} alignItems="center">
                                <Avatar.Root size="sm">
                                    <Avatar.Fallback name={name} />
                                    <Avatar.Image src={avatar} />
                                </Avatar.Root>
                                <Text
                                  fontSize="sm"
                                  fontWeight="500"
                                  color={colorMode === 'dark' ? 'gray.100' : 'gray.700'}
                                  display={{ base: 'none', md: 'inline' }}
                                >
                                    {name}
                                </Text>
                            </HStack>
                            <Button
                                as={Link}
                                to="/dashboard"
                                colorPalette="brand"
                                variant="outline"
                                size="md"
                                width={isMobile ? '40px' : undefined}
                                minWidth={isMobile ? '40px' : undefined}
                                justifyContent="center"
                                p={isMobile ? 0 : undefined}
                            >
                                <FiGrid />
                                {!isMobile && ' Dashboard'}
                            </Button>
                            <Button
                                onClick={handleLogout}
                                colorPalette="red"
                                variant="outline"
                                size="md"
                                width={isMobile ? '40px' : undefined}
                                minWidth={isMobile ? '40px' : undefined}
                                justifyContent="center"
                                p={isMobile ? 0 : undefined}
                            >
                                <FiLogOut />
                                {!isMobile && ' Logout'}
                            </Button>
                        </HStack>
                    ) : (
                        <Button
                            as={Link}
                            to="/login"
                            colorPalette="blue"
                            variant="outline"
                            size="md"
                            width={isMobile ? '40px' : undefined}
                            minWidth={isMobile ? '40px' : undefined}
                            justifyContent="center"
                            p={isMobile ? 0 : undefined}
                        >
                            <FiLogIn />
                            {!isMobile && ' Login'}
                        </Button>
                    )}
                    {/* Cart icon - always show when items are in cart */}
                    {getItemCount() > 0 && (
                        <Box position="relative">
                            <IconButton
                                as={Link}
                                to="/cart"
                                variant="ghost"
                                size="md"
                                aria-label="Shopping Cart"
                                color="orange.500"
                                _hover={{
                                    bg: colorMode === 'dark' ? 'gray.700' : 'gray.100',
                                    transform: 'scale(1.05)'
                                }}
                                _active={{
                                    transform: 'scale(0.95)'
                                }}
                                transition="all 0.2s"
                                borderRadius="lg"
                                p={2}
                                display="flex"
                                visibility="visible"
                                opacity={1}
                                zIndex={1000}
                            >
                                <FaShoppingCart size={18} />
                            </IconButton>
                            <Badge
                                position="absolute"
                                top="-2px"
                                right="-2px"
                                colorPalette="red"
                                borderRadius="full"
                                fontSize="10px"
                                minW="20px"
                                h="20px"
                                display="flex"
                                alignItems="center"
                                justifyContent="center"
                                fontWeight="bold"
                                boxShadow="0 0 0 2px white"
                                zIndex={1}
                            >
                                {getItemCount()}
                            </Badge>
                        </Box>
                    )}
                    {/* Notification icon, hidden in mobile mode */}
                    {!isMobile && (
                    <Box position="relative">
                        <IconButton
                            as={Link}
                            to="/my-tests"
                            variant="ghost"
                            size="md"
                            aria-label="Notifications"
                            color="orange.500"
                            _hover={{
                                bg: colorMode === 'dark' ? 'gray.700' : 'gray.100',
                                transform: 'scale(1.05)'
                            }}
                            _active={{
                                transform: 'scale(0.95)'
                            }}
                            transition="all 0.2s"
                            borderRadius="lg"
                            p={2}
                        >
                            <LuBell size={20} />
                        </IconButton>
                        {hasNewExamNotification && (
                            <Box
                                position="absolute"
                                top="-2px"
                                right="-2px"
                                width="8px"
                                height="8px"
                                bg="orange.400"
                                borderRadius="full"
                                border="2px solid white"
                                zIndex={1}
                            />
                        )}
                    </Box>
                    )}
                </HStack>
            </Flex>
        </Box>
    );
};

export default Header; 
